/** @jsx jsx */
import { jsx } from '@balance-web/core';
import type { RefObject } from 'react';
import React from 'react';
import { DeprecatedButton } from '@balance-web/button-deprecated';
import { Text } from '@balance-web/text';
import { Heading } from '@balance-web/heading';
import { Divider } from '@balance-web/divider';
import { Notice } from '@balance-web/notice';
import { useTheme } from '@balance-web/theme';
import { Flex } from '@balance-web/flex';

import { TextField } from '../../generic/TextField';
import { WidgetContainer } from '../../generic/wigetContainer';

import type { SignInFormInstance } from './SignInFormSchema';
import type { SubmitError } from './types';
import { SingleSignOnButtons } from './singleSignOnButtons';

type SignInProps = {
  form: SignInFormInstance;
  submitAction: {
    onClick: () => void;
    error?: SubmitError;
    loading: boolean;
    disabled?: boolean;
  };
  SingleSignOnActions?: {
    microsoft?: { onClick: () => void };
    google?: { onClick: () => void };
    apple?: { onClick: () => void };
  };
  navigation?: {
    navigateToCreateAccount?: () => void;
    navigateToForgotPassword?: () => void;
  };
  resetPasswordSuccess: boolean;
  passwordInputRef: RefObject<HTMLInputElement>;
  loggedout?: boolean;
  message?: string;
  statusType?: 'reset_password' | 'register' | 'none';
};

export const SignInForm = ({
  form,
  submitAction,
  SingleSignOnActions,
  navigation,
  resetPasswordSuccess,
  passwordInputRef,
  loggedout,
  message,
  statusType,
}: SignInProps) => {
  const { spacing } = useTheme();

  return (
    <WidgetContainer>
      <Flex direction="column" gap="xlarge" margin="xxlarge">
        <>
          <Heading align="center" level="6">
            Sign in to your Reckon account
          </Heading>

          {!!message && statusType === 'none' && (
            <Notice size="small" tone="cautious">
              <Text size="small">{message}</Text>
            </Notice>
          )}
          {!!message && statusType === 'reset_password' && navigation && (
            <Notice
              actions={{
                primary: {
                  label: 'Reset your password',
                  action: () => {
                    if (navigation.navigateToForgotPassword) {
                      navigation.navigateToForgotPassword();
                    }
                  },
                },
              }}
              size="small"
              tone="cautious"
            >
              <Text>{message}</Text>
            </Notice>
          )}
          {!!message && statusType === 'register' && navigation && (
            <Notice
              actions={{
                primary: {
                  label: 'Sign up for an account',
                  action: () => {
                    if (navigation.navigateToCreateAccount) {
                      navigation.navigateToCreateAccount();
                    }
                  },
                },
              }}
              size="small"
              tone="cautious"
            >
              <Text>{message}</Text>
            </Notice>
          )}
          {loggedout && (
            <Notice tone="informative">
              You are required by the ATO to sign in after 30 minutes of
              inactivity
            </Notice>
          )}

          {resetPasswordSuccess && (
            <Notice tone="positive">
              <Text>
                Password has been reset. Please sign in with your new password.
              </Text>
            </Notice>
          )}

          <Flex
            as="form"
            direction="column"
            gap="large"
            marginTop="small"
            onSubmit={submitAction.onClick}
          >
            <TextField
              label="Email address or username"
              placeholder="Enter email or username"
              {...form.fields.emailOrUsername.props}
            />

            <TextField
              ref={passwordInputRef}
              label="Password"
              placeholder="Enter password"
              type="password"
              {...form.fields.password.props}
            />

            <DeprecatedButton
              block
              disabled={submitAction?.disabled}
              label="Continue"
              loading={submitAction.loading}
              type="submit"
            />
          </Flex>
        </>

        {SingleSignOnActions && (
          <Flex direction="column" gap="xlarge">
            <Text align="center">- OR -</Text>
            <SingleSignOnButtons {...SingleSignOnActions} />
          </Flex>
        )}

        {navigation && (
          <>
            <Divider />
            <Flex direction="column" marginTop="medium">
              {navigation.navigateToCreateAccount && (
                <Flex
                  alignItems="center"
                  alignSelf="center"
                  gap="medium"
                  marginTop="medium"
                >
                  <Text align="center">New to Reckon?</Text>
                  <DeprecatedButton
                    css={{ padding: spacing.small }}
                    label="Create a new account"
                    weight="none"
                    onClick={navigation.navigateToCreateAccount}
                  />
                </Flex>
              )}

              {navigation.navigateToForgotPassword && (
                <DeprecatedButton
                  block
                  label="Forgotten password"
                  weight="none"
                  onClick={navigation.navigateToForgotPassword}
                />
              )}
            </Flex>
          </>
        )}
      </Flex>
    </WidgetContainer>
  );
};
